import { useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Sidebar } from "./components/Sidebar";
import { TopBar } from "./components/TopBar";
import { MobileHeader } from "./components/MobileHeader";
import { InstallPrompt } from "./components/InstallPrompt";
import { PWAUpdatePrompt } from "./components/PWAUpdatePrompt";

import { SecurityHeaders } from "./components/SecurityHeaders";
import { GuestSignupModal } from "./components/GuestSignupModal";
import { GuestTrialBanner } from "./components/GuestTrialBanner";
import { useGuestTrial } from "./hooks/useGuestTrial";
import { initializeOneSignal } from "./lib/onesignal";
import AuthForm from "./components/AuthForm";
import Chat from "./pages/Chat";
import GuestChat from "./pages/GuestChat";
import Receipts from "./pages/Receipts";
import Summary from "./pages/Summary";
import Settings from "./pages/Settings";
import Install from "./pages/Install";
import Onboarding from "./pages/Onboarding";
import NotFound from "./pages/NotFound";
import NewReceiptScanner from "./pages/NewReceiptScanner";
import ReceiptDetail from "./pages/ReceiptDetail";
import ReceiptEdit from "./pages/ReceiptEdit";
import AdminUsageDashboard from "./pages/AdminUsageDashboard";

const queryClient = new QueryClient();

const AppContent = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showSignupModal, setShowSignupModal] = useState(false);
  
  const { 
    isGuest, 
    trialExpired, 
    formatRemainingTime, 
    startGuestTrial, 
    clearGuestTrial 
  } = useGuestTrial();

  useEffect(() => {
    // Check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
      
      // If no user, start guest trial automatically
      if (!session?.user) {
        startGuestTrial();
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        clearGuestTrial();
      }
    });

    return () => subscription.unsubscribe();
  }, [startGuestTrial, clearGuestTrial]);

  useEffect(() => {
    // Initialize OneSignal when user is logged in
    if (user) {
      initializeOneSignal();
    }
  }, [user]);

  // Show signup modal when trial expires
  useEffect(() => {
    if (trialExpired && !user) {
      setShowSignupModal(true);
    }
  }, [trialExpired, user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Guest mode: Full app access for 2 minutes
  if (!user && isGuest && !trialExpired) {
    return (
      <BrowserRouter>
        <div className="flex h-screen w-full bg-white overflow-hidden">
          {/* Guest Trial Banner */}
          <div className="fixed top-0 left-0 right-0 z-50 md:left-64">
            <GuestTrialBanner 
              onSignUp={() => setShowSignupModal(true)} 
            />
          </div>
          
          {/* Fixed Sidebar - hidden on mobile, visible on desktop */}
          <div className="hidden md:block md:fixed md:inset-y-0 md:left-0 md:z-40">
            <Sidebar />
          </div>
          
          {/* Main content area */}
          <div className="flex-1 flex flex-col h-screen md:ml-64 bg-white">
            {/* Fixed Mobile Header */}
            <div className="mt-10 md:mt-0">
              <MobileHeader />
            </div>
            
            {/* Fixed Desktop Top Bar */}
            <div className="hidden md:block mt-10">
              <TopBar />
            </div>
            
            {/* Scrollable content area */}
            <main className="flex-1 overflow-y-auto bg-white">
              <Routes>
                <Route path="/" element={<GuestChat />} />
                <Route path="/receipts" element={<Receipts />} />
                <Route path="/receipts/new" element={<NewReceiptScanner />} />
                <Route path="/receipt/:id" element={<ReceiptDetail />} />
                <Route path="/receipt/:id/edit" element={<ReceiptEdit />} />
                <Route path="/summary" element={<Summary />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/install" element={<Install />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </main>
          </div>
          <InstallPrompt />
          
          {/* Signup Modal */}
          <GuestSignupModal 
            open={showSignupModal} 
            onOpenChange={setShowSignupModal} 
          />
        </div>
      </BrowserRouter>
    );
  }

  // Trial expired: Show signup modal
  if (!user && isGuest && trialExpired) {
    return (
      <BrowserRouter>
        <main>
          <AuthForm />
          <GuestSignupModal 
            open={true} 
            onOpenChange={() => {}} 
          />
        </main>
      </BrowserRouter>
    );
  }

  // Redirect to auth form if no user and not in guest mode
  if (!user) {
    return (
      <BrowserRouter>
        <main>
          <AuthForm />
        </main>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <div className="flex h-screen w-full bg-white overflow-hidden">
        {/* Fixed Sidebar - hidden on mobile, visible on desktop */}
        <div className="hidden md:block md:fixed md:inset-y-0 md:left-0 md:z-50">
          <Sidebar />
        </div>
        
        {/* Main content area with fixed headers */}
        <div className="flex-1 flex flex-col h-screen md:ml-64 bg-white">
          {/* Fixed Mobile Header */}
          <MobileHeader />
          
          {/* Fixed Desktop Top Bar */}
          <TopBar />
          
          {/* Scrollable content area */}
          <main className="flex-1 overflow-y-auto bg-white">
            <Routes>
              <Route path="/" element={<Chat />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/receipts" element={<Receipts />} />
              <Route path="/receipts/new" element={<NewReceiptScanner />} />
              <Route path="/receipt/:id" element={<ReceiptDetail />} />
              <Route path="/receipt/:id/edit" element={<ReceiptEdit />} />
              <Route path="/summary" element={<Summary />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/install" element={<Install />} />
              <Route path="/admin/usage" element={<AdminUsageDashboard />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
        </div>
        <InstallPrompt />
        
      </div>
    </BrowserRouter>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <SecurityHeaders />
        <PWAUpdatePrompt />
        <Toaster />
        <Sonner />
        <AppContent />
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
